import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import QRCode from 'qrcode';
import { formatSubscriptionDate, getAcademicYearPeriod } from './academicYear';
import { normalizeInvoiceIssuer } from '../constants/invoiceIssuer';
import { exportFile } from './nativeExport';

const MARGIN = 48;
const PRIMARY = [0, 80, 179];

/** jsPDF Helvetica is ASCII-only — avoid ₹ and Unicode dashes in PDF text. */
function pdfText(str) {
  return String(str ?? '')
    .replace(/\u20B9/g, 'Rs. ')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u00A0/g, ' ');
}

function formatInr(amount) {
  const n = Number(amount) || 0;
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatInrPdf(amount) {
  return `Rs. ${formatInr(amount)}`;
}

function amountInWordsSimple(amount) {
  const n = Math.round(Number(amount) || 0);
  if (n <= 0) return '';
  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function under1000(num) {
    if (num < 20) return ones[num];
    if (num < 100) {
      const t = Math.floor(num / 10);
      const r = num % 10;
      return `${tens[t]}${r ? ` ${ones[r]}` : ''}`.trim();
    }
    const h = Math.floor(num / 100);
    const r = num % 100;
    return `${ones[h]} Hundred${r ? ` ${under1000(r)}` : ''}`.trim();
  }

  function convert(num) {
    if (num < 1000) return under1000(num);
    if (num < 100000) {
      const th = Math.floor(num / 1000);
      const r = num % 1000;
      return `${under1000(th)} Thousand${r ? ` ${under1000(r)}` : ''}`.trim();
    }
    const lk = Math.floor(num / 100000);
    const r = num % 100000;
    return `${under1000(lk)} Lakh${r ? ` ${convert(r)}` : ''}`.trim();
  }

  return `Rupees ${convert(n)} Only`;
}

export function buildInvoiceData(head, options = {}) {
  const issuer = normalizeInvoiceIssuer(options.issuer);
  const qty = Math.max(
    0,
    parseInt(options.lineQty ?? head?.divisions, 10) || 0
  );
  const rate = Number(options.lineRate ?? issuer.pricePerClassYear) || issuer.pricePerClassYear;
  const amount = qty * rate;
  const invoiceDate = String(options.invoiceDate || new Date().toISOString().slice(0, 10)).slice(0, 10);
  const period = getAcademicYearPeriod(head?.subscription_expires_at);
  const periodStart = String(options.periodStart || period.periodStart).slice(0, 10);
  const periodEnd = String(options.periodEnd || period.periodEnd).slice(0, 10);
  const username = head?.username || 'school';

  return {
    issuer,
    invoiceNumber: options.invoiceNumber || `NM-${username}-${invoiceDate.replace(/-/g, '')}`,
    invoiceDate,
    billToName: head?.name || '',
    billToCode: username,
    periodStart,
    periodEnd,
    lineItems: [
      {
        description: String(options.lineDescription || issuer.lineItemDescription),
        qty,
        rate,
        amount,
      },
    ],
    total: amount,
    amountInWords: amountInWordsSimple(amount),
  };
}

function wrapText(doc, text, x, y, maxWidth, lineHeight = 12) {
  const lines = doc.splitTextToSize(pdfText(text), maxWidth);
  lines.forEach((line, i) => {
    doc.text(line, x, y + i * lineHeight);
  });
  return y + lines.length * lineHeight;
}

function drawLabelledBlock(doc, { x, y, width, title, lines }) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(title, x, y);
  let cy = y + 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  (lines || []).filter(Boolean).forEach((line, i) => {
    if (i === 0) {
      doc.setFont('helvetica', 'bold');
      cy = wrapText(doc, line, x, cy, width, 12);
      doc.setFont('helvetica', 'normal');
    } else {
      cy = wrapText(doc, line, x, cy, width, 11);
    }
  });
  return cy;
}

/** NPCI UPI intent URI — dynamic QR with payee, amount, and unique transaction ref. */
function buildUpiPayUri({ upiId, payeeName, amount, note, transactionRef }) {
  const pa = String(upiId || '').trim();
  if (!pa) return '';
  const parts = [`pa=${encodeURIComponent(pa)}`];
  const pn = String(payeeName || '').trim();
  if (pn) parts.push(`pn=${encodeURIComponent(pn.slice(0, 50))}`);
  const am = Number(amount);
  if (am > 0) parts.push(`am=${am.toFixed(2)}`);
  parts.push('cu=INR');
  const tr = String(transactionRef || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 35);
  if (tr) parts.push(`tr=${encodeURIComponent(tr)}`);
  const tn = String(note || '').trim();
  if (tn) parts.push(`tn=${encodeURIComponent(tn.slice(0, 80))}`);
  return `upi://pay?${parts.join('&')}`;
}

export async function createInvoicePdfBlob(invoiceData) {
  const data = invoiceData;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const colHalf = (pageW - MARGIN * 2) / 2;
  const billX = MARGIN + colHalf + 16;
  let y = 52;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(0, 0, 0);
  doc.text('INVOICE', MARGIN, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(pdfText(`Invoice #: ${data.invoiceNumber}`), pageW - MARGIN, y - 6, { align: 'right' });
  doc.text(pdfText(`Date: ${formatSubscriptionDate(data.invoiceDate)}`), pageW - MARGIN, y + 8, { align: 'right' });

  y += 32;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, pageW - MARGIN, y);
  y += 20;

  const fromLines = [
    data.issuer.issuerName,
    data.issuer.issuerAddress,
    data.issuer.issuerEmail,
    data.issuer.issuerEmail2,
    data.issuer.issuerPhone,
    data.issuer.upiId ? `UPI: ${data.issuer.upiId}` : '',
  ];
  const billLines = [data.billToName, `@${data.billToCode}`];

  const fromEndY = drawLabelledBlock(doc, {
    x: MARGIN,
    y,
    width: colHalf - 8,
    title: 'From',
    lines: fromLines,
  });
  const billEndY = drawLabelledBlock(doc, {
    x: billX,
    y,
    width: colHalf - 8,
    title: 'Bill To',
    lines: billLines,
  });

  y = Math.max(fromEndY, billEndY) + 18;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Service period', MARGIN, y);
  doc.setFont('helvetica', 'normal');
  doc.text(
    pdfText(`${formatSubscriptionDate(data.periodStart)} - ${formatSubscriptionDate(data.periodEnd)}`),
    MARGIN + 82,
    y
  );

  y += 22;
  const row = data.lineItems[0] || { description: '', qty: 0, rate: 0, amount: 0 };

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'plain',
    head: [[
      'Description',
      { content: 'Qty', styles: { halign: 'center' } },
      { content: 'Rate (Rs)', styles: { halign: 'right' } },
      { content: 'Amount (Rs)', styles: { halign: 'right' } },
    ]],
    body: [[
      pdfText(row.description),
      String(row.qty),
      formatInr(row.rate),
      formatInrPdf(row.amount),
    ]],
    foot: [[
      { content: 'Total', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold', fillColor: [245, 247, 250] } },
      { content: formatInrPdf(data.total), styles: { halign: 'right', fontStyle: 'bold', fillColor: [245, 247, 250] } },
    ]],
    styles: {
      fontSize: 9,
      cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
      lineColor: [210, 210, 210],
      lineWidth: 0.5,
      valign: 'middle',
    },
    headStyles: {
      fillColor: PRIMARY,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
    },
    footStyles: {
      fontSize: 10,
      textColor: 0,
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { halign: 'center', cellWidth: 44 },
      2: { halign: 'right', cellWidth: 76 },
      3: { halign: 'right', cellWidth: 92 },
    },
  });

  y = (doc.lastAutoTable?.finalY ?? y) + 14;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  if (data.amountInWords) {
    doc.text(pdfText(data.amountInWords), MARGIN, y);
    y += 18;
  }

  y += 6;
  doc.setTextColor(0, 0, 0);
  const qrSize = 96;
  const hasUpiQr = Boolean(data.issuer.upiId?.trim()) && data.total > 0;
  const paymentTextWidth = hasUpiQr ? pageW - MARGIN * 2 - qrSize - 20 : pageW - MARGIN * 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Payment', MARGIN, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  y += 14;
  const paymentContentY = y;

  if (data.issuer.upiId) {
    y = wrapText(doc, `UPI ID: ${data.issuer.upiId}`, MARGIN, y, paymentTextWidth, 11);
    y += 2;
  }
  y = wrapText(doc, data.issuer.paymentTerms, MARGIN, y, paymentTextWidth, 11);
  if (data.issuer.bankDetails) {
    y = wrapText(doc, data.issuer.bankDetails, MARGIN, y + 2, paymentTextWidth, 11);
  }

  if (hasUpiQr) {
    const uri = buildUpiPayUri({
      upiId: data.issuer.upiId,
      payeeName: data.issuer.issuerName,
      amount: data.total,
      note: `NMhelper ${data.invoiceNumber}`,
      transactionRef: data.invoiceNumber,
    });
    try {
      const qrDataUrl = await QRCode.toDataURL(uri, {
        width: 280,
        margin: 2,
        errorCorrectionLevel: 'M',
      });
      const qrX = pageW - MARGIN - qrSize;
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.rect(qrX - 4, paymentContentY - 4, qrSize + 8, qrSize + 8);
      doc.addImage(qrDataUrl, 'PNG', qrX, paymentContentY, qrSize, qrSize);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text('Scan to pay (UPI)', qrX + qrSize / 2, paymentContentY + qrSize + 12, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.text(formatInrPdf(data.total), qrX + qrSize / 2, paymentContentY + qrSize + 22, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
    } catch (err) {
      console.warn('UPI QR generation failed', err);
    }
    y = Math.max(y, paymentContentY + qrSize + 30);
  }

  y += 14;
  doc.setFontSize(8);
  doc.setTextColor(110, 110, 110);
  y = wrapText(doc, data.issuer.footerNote, MARGIN, y, pageW - MARGIN * 2, 10);
  doc.text('Thank you for your business.', MARGIN, y + 12);

  return doc.output('blob');
}

export async function downloadInvoicePdf(invoiceData) {
  const blob = await createInvoicePdfBlob(invoiceData);
  const fileName = `Invoice-${invoiceData.billToCode}-${invoiceData.invoiceDate}.pdf`;
  await exportFile({ blob, fileName, mimeType: 'application/pdf' });
}

export async function zipInvoicePdfs(invoiceDataList) {
  const zip = new JSZip();
  const date = new Date().toISOString().slice(0, 10);
  await Promise.all(
    invoiceDataList.map(async (data) => {
      const blob = await createInvoicePdfBlob(data);
      const buf = await blob.arrayBuffer();
      zip.file(`Invoice-${data.billToCode}-${data.invoiceDate}.pdf`, buf);
    })
  );
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  await exportFile({
    blob: zipBlob,
    fileName: `invoices-${date}.zip`,
    mimeType: 'application/zip',
  });
}
